/**
 * `cusa-petition-funnel-analytics.js`
 *
 * Owns every GA4 event for the petition funnel (sign → support → share) plus the
 * fire-once rules that keep the counts honest. Centralised deliberately: the funnel's
 * state transitions happen across three separate files, and scattering the dedupe
 * logic across them is how the previous `petition_funnel_view_2` schema drifted into
 * behaving like a page counter.
 *
 * Firing rules:
 * - `sign` — deduped per session + petition. A guest who signs, then refreshes and gets
 *   bounced back to the sign view, must not re-count the step; it is the signature-rate
 *   denominator.
 * - `support` / `share` entered passively (a direct page load, e.g. a logged-in member
 *   routed straight to support) — deduped per session + petition + step, so refreshing
 *   in place doesn't re-count.
 * - `support` / `share` entered via an action (a signature advancing to support, a click
 *   through to share) — always fires. A guest's second signature is a legitimate new
 *   transition even though support already fired earlier in the session. The rule is
 *   "suppress passive reloads", not "suppress anything already seen".
 * - Backward and step-map navigation never fire. Re-opening a card the user has already
 *   seen is not a step entry.
 *
 * The petition is keyed on pathname — the funnel views render on the same URL as the
 * landing page, so pathname uniquely identifies the petition on every funnel event.
 */
if (!window.CusaPetitionFunnelAnalytics) {
	const STORAGE_KEY = 'CUSA: petition funnel steps viewed';

	/**
	 * Entry reasons representing a passive arrival (a page load) rather than a user
	 * action. These are deduped per session; action-driven entries never are.
	 * @type {string[]}
	 */
	const PASSIVE_ENTRIES = ['direct_logged_in', 'direct_returning', 'direct_returning_cookie'];

	/**
	 * The petition key for the current page. Funnel steps are client-side state on a
	 * single URL, so pathname identifies the petition on every event.
	 * @returns {string}
	 */
	const petitionKey = () => window.location.pathname;

	/**
	 * @returns {Record<string, boolean>}
	 */
	const readSeen = () => {
		try {
			return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
		} catch (error) {
			return {};
		}
	};

	/**
	 * Records a token as seen this session. Returns false if it was already recorded,
	 * which is the caller's signal to suppress the event.
	 *
	 * Fails open: if sessionStorage is unavailable (Safari private mode, storage
	 * partitioning), we let the event through rather than silently dropping funnel data.
	 *
	 * @param {string} token
	 * @returns {boolean} True when this is the first time the token has been seen.
	 */
	const markSeen = (token) => {
		const seen = readSeen();
		if (seen[token]) return false;
		seen[token] = true;
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
		} catch (error) {
			// Storage unavailable — allow the event rather than suppressing it.
		}
		return true;
	};

	/**
	 * @param {string} eventName
	 * @param {Record<string, any>} payload
	 * @returns {void}
	 */
	const send = (eventName, payload) => {
		if ('function' !== typeof sendAnalyticsEvent) return;
		// `item_id` rides on every funnel event so a petition can be identified without
		// falling back to page path. It is set here rather than per-event so the three
		// call sites can't drift apart.
		// sendAnalyticsEvent() strips null/undefined/'' so unused params drop out
		// rather than reaching GA4 as empty strings.
		sendAnalyticsEvent(eventName, {
			item_id: 'function' === typeof getSlug ? getSlug(window.location.href) : '',
			...payload
		});
	};

	const CusaPetitionFunnelAnalytics = {
		/**
		 * Fires `petition_step_view` for a funnel step entry, subject to the fire-once
		 * rules above.
		 *
		 * @param {'sign'|'support'|'share'} step
		 * @param {string=} entryReason - Required for `support`; one of after_new_signature,
		 *   after_duplicate_signature, direct_logged_in, direct_returning. Omitted on
		 *   sign and share, which carry no `step_entry`.
		 * @returns {boolean} Whether the event was actually sent.
		 */
		trackStepView(step, entryReason) {
			const token = `${petitionKey()}|${step}`;
			// `support` is the one step with a legitimate repeat transition — a guest's
			// second signature — so it is only deduped when the arrival was passive.
			// `sign` and `share` are deduped outright: re-entering share after backing
			// out is not new funnel progression, and counting it is the toggle inflation
			// this rebuild exists to remove.
			const isPassive = step !== 'support' || PASSIVE_ENTRIES.includes(entryReason);

			if (isPassive && !markSeen(token)) return false;

			send('petition_step_view', {
				funnel_step: step,
				step_entry: step === 'support' ? entryReason : undefined
			});
			return true;
		},

		/**
		 * Fires the `sign` step view once the arrival state is actually known.
		 *
		 * Guests always see the sign view — Guardian data only recognises members — so
		 * their event fires immediately. A logged-in member who has already signed is
		 * routed straight to support and never sees the sign view; firing for them would
		 * inflate the signature-rate denominator. That routing is decided asynchronously
		 * when Guardian data resolves, so for members we wait for that determination
		 * before deciding whether a sign view actually happened.
		 *
		 * @param {boolean} isLoggedIn
		 * @returns {void}
		 */
		trackSignStep(isLoggedIn) {
			const fire = () => {
				if (document.forms.sign?.dataset.hasSigned === 'true') return;
				this.trackStepView('sign');
			};

			if (!isLoggedIn) {
				fire();
				return;
			}

			let settled = false;
			const settle = () => {
				if (settled) return;
				settled = true;
				// Yield a macrotask so sign-state.js's own guardianDataSet subscriber runs
				// first — it's what sets hasSigned, and subscriber order isn't guaranteed.
				setTimeout(fire, 0);
			};

			const subscribeFn = typeof window.subscribe === 'function' ? window.subscribe : null;
			if (subscribeFn && window.PUB_SUB_EVENTS?.guardianDataSet) {
				subscribeFn(window.PUB_SUB_EVENTS.guardianDataSet, settle);
			} else {
				window.addEventListener('guardianDataSet', settle);
			}
			// Fallback so a member whose Guardian data never arrives still counts.
			setTimeout(settle, 3000);
		},

		/**
		 * Fires `petition_signature`.
		 * @param {'new'|'duplicate'} result
		 * @returns {void}
		 */
		trackSignature(result) {
			send('petition_signature', { signature_result: result });
		},

		/**
		 * Fires `select_contribution` for a contribution-tier click.
		 * @param {string|number} amount - Dollar value; accepts '$5', '5', or 5.
		 * @returns {void}
		 */
		trackContribution(amount) {
			const parsed = parseFloat(String(amount).replace(/[^0-9.]/g, ''));
			if (!Number.isFinite(parsed)) return;
			// Sent twice under two names on purpose: GA4 will not accept one parameter as
			// both a custom dimension and a custom metric, and we want the tier breakdown
			// (dimension) as well as summed dollars (metric).
			send('select_contribution', { contribution_amount: parsed, contribution_value: parsed });
		}
	};

	window.CusaPetitionFunnelAnalytics = CusaPetitionFunnelAnalytics;
}
